import { Box } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import ReactFlow, {
  DefaultEdgeOptions,
  MarkerType,
  NodeMouseHandler,
  NodeTypes,
} from 'reactflow';
import { v4 as uuidv4 } from 'uuid';

import { DbTable } from '@finley-technologies/finley-types';

import { useInstanceGraph } from '../../hooks/instanceGraphContext';
import { NodeWithHandles } from './NodeWithHandles';
import { repositionGraph } from 'packages/common-frontend/components/ReactFlow/layout/d3hierarchy';

import 'reactflow/dist/style.css';

const defaultViewport = { x: 0, y: 0, zoom: 2 }; // start somewhat zoomed out.

const defaultEdgeOptions: DefaultEdgeOptions = {
  animated: true,
  style: { strokeWidth: 2 }, // Adjust strokeWidth and stroke color here

  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
};

const proOptions = {
  hideAttribution: true,
};

const nodeTypes: NodeTypes = DbTable.ArtifactTypeIterable.reduce<NodeTypes>(
  (acc, artifactType) => ({
    ...acc,
    [artifactType]: NodeWithHandles,
  }),
  {},
);

export const GraphRenderer = () => {
  const { nodes, edges, setSelectedNode, selectedNodeId } = useInstanceGraph();

  const initialNodes = nodes.map((node) => {
    return {
      id: node.artifact.id,
      type: node.artifact.type,
      data: node.artifact.id,
    };
  });

  const initialEdges: {
    id: string;
    source: string;
    target: string;
  }[] = edges.map((edge) => {
    return {
      id: uuidv4(),
      source: edge.source,
      target: edge.target,
    };
  });

  const positionedNodes = repositionGraph({
    nodes: initialNodes,
    edges: initialEdges,
    xSpacing: 400,
    ySpacing: 300,
    disconnectedSpacing: 400,
  });

  const onNodeClick = useCallback<NodeMouseHandler>(
    (_, node) => {
      if (node && node.id !== selectedNodeId) {
        setSelectedNode(node.id);
      }
    },
    [selectedNodeId, setSelectedNode],
  );

  return (
    <Box
      resize="vertical"
      overflow="auto"
      width="100%"
      height={800}
      border="1px solid #ccc"
    >
      <ReactFlow
        disableKeyboardA11y
        nodeTypes={nodeTypes}
        defaultNodes={positionedNodes}
        defaultEdges={initialEdges}
        elevateEdgesOnSelect
        defaultEdgeOptions={defaultEdgeOptions}
        onNodeClick={onNodeClick}
        defaultViewport={defaultViewport}
        fitView
        minZoom={0.1}
        maxZoom={50}
        proOptions={proOptions}
        selectionKeyCode={null}
        deleteKeyCode={null}
        elementsSelectable={false}
      />
    </Box>
  );
};
